// biome-ignore-all lint/a11y/noLabelWithoutControl: each <label> captions the custom input directly beside it in the grid (the codebase's form-file convention; same as ByodLookupPanel).
// =============================================================================
// ByodBandEditor.jsx — the shared editable BYOD-band field grid (BYOD Part 7)
// -----------------------------------------------------------------------------
// ONE editable grid for a single BYOD policy band, reused by BOTH:
//   • the responder ByodLookupPanel (one child + one covering band), and
//   • the admin SchoolByodManager (school-level, one row per active band, and
//     several at once from a whole-school lookup).
//
// It renders ONLY the field grid — status / device / year band / loan / hardship
// / loan note / policy URL / notes — driven by a caller-owned `edited` draft plus
// a `setField(key, value)` callback. The surrounding chrome (the AI-verify header,
// the sources list, the error line, and the Save / Cancel buttons) stays with each
// caller, because it differs between the responder review panel and the admin list.
//
// The draft helpers (emptyBandDraft / bandDraftFromBand / bandDraftFromSuggestion),
// the year-band validator (validateBandDraft) and the EF payload shaper
// (bandDraftToFields) live here too, so both callers seed, validate and save an
// identical draft shape. Extracted verbatim from ByodLookupPanel so behaviour is
// unchanged.
// =============================================================================

export const TEAL = "#00897B";

const LABEL = {
  fontSize: 11,
  color: "#607D79",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: 3,
  display: "block",
};
const INPUT = {
  width: "100%",
  padding: "7px 10px",
  borderRadius: 6,
  border: "1px solid #CFE0DE",
  fontSize: 13,
  fontFamily: "inherit",
  boxSizing: "border-box",
  background: "#fff",
};

// A year band → a human label. Prep = 0 … Year 12 = 12.
export function bandLabel(from, to) {
  if (from == null || to == null) return "—";
  const name = (n) => (n === 0 ? "Prep" : `Year ${n}`);
  return from === to ? name(from) : `${name(from)}–${name(to)}`;
}

// A well-formed http(s) URL → true (so we only offer a clickable "Open" link for a real link).
export function isHttpUrl(v) {
  if (typeof v !== "string" || !v.trim()) return false;
  try {
    const u = new URL(v.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

// ── draft helpers (identical draft shape for both callers) ───────────────────

// A blank editable draft, optionally seeded with a known year band.
export function emptyBandDraft(defaultFrom = null, defaultTo = null) {
  return {
    status: "unknown",
    device: "",
    year_levels: "",
    year_from: defaultFrom,
    year_to: defaultTo,
    loan: null,
    loan_note: "",
    hardship: null,
    policy_url: "",
    notes: "",
  };
}

// Seed a draft from a stored school_byod band row (the "Edit / view" path).
export function bandDraftFromBand(band) {
  return {
    status: band.byod_status || "unknown",
    device: band.byod_device || "",
    year_levels: "",
    year_from: band.year_from,
    year_to: band.year_to,
    loan: band.byod_loan,
    loan_note: band.byod_loan_note || "",
    hardship: band.byod_hardship,
    policy_url: band.byod_policy_url || "",
    notes: band.byod_notes || "",
  };
}

// Seed a draft from an AI suggestion; the band bounds fall back to `fallbackYear`
// (the child's own year in the responder panel; null on the admin whole-school path,
// where the AI returns explicit bounds).
export function bandDraftFromSuggestion(s, fallbackYear = null) {
  return {
    status: s.status || "unknown",
    device: s.device || "",
    year_levels: s.year_levels || "",
    year_from: s.year_from != null ? s.year_from : fallbackYear,
    year_to: s.year_to != null ? s.year_to : fallbackYear,
    loan: s.loan,
    loan_note: s.loan_note || "",
    hardship: s.hardship,
    policy_url: s.policy_url || "",
    notes: s.notes || "",
  };
}

// Validate the draft's year band. { ok:true, from, to } or { ok:false, error }.
// Null / blank bounds are rejected explicitly — Number(null) and Number("") both
// coerce to 0, so without this a missing year would slip through as a Prep (0–0) band.
export function validateBandDraft(edited) {
  const blank = (v) => v == null || v === "";
  const from = Number(edited.year_from);
  const to = Number(edited.year_to);
  if (
    blank(edited.year_from) ||
    blank(edited.year_to) ||
    !Number.isInteger(from) ||
    !Number.isInteger(to) ||
    from < 0 ||
    to > 12 ||
    from > to
  ) {
    return { ok: false, error: "Enter a valid year band (0–12, from ≤ to) before saving." };
  }
  return { ok: true, from, to };
}

// The policy attributes the EF `save` / `save_bands` action takes as `fields`
// (band bounds are separate save params, so they are NOT in here).
export function bandDraftToFields(edited) {
  return {
    status: edited.status,
    device: edited.device,
    loan: edited.loan,
    loan_note: edited.loan_note,
    hardship: edited.hardship,
    policy_url: edited.policy_url,
    notes: edited.notes,
  };
}

// ── the shared editable grid ─────────────────────────────────────────────────
function ByodBandEditor({ edited, setField }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      <div>
        <label style={LABEL}>Status</label>
        <select
          style={INPUT}
          value={edited.status}
          onChange={(e) => setField("status", e.target.value)}
        >
          <option value="byod">BYOD (student brings a device)</option>
          <option value="school_supplied">School-supplied</option>
          <option value="unknown">Unknown</option>
        </select>
      </div>
      <div>
        <label style={LABEL}>Device</label>
        <input
          style={INPUT}
          value={edited.device}
          onChange={(e) => setField("device", e.target.value)}
        />
      </div>
      <div>
        <label style={LABEL}>Year band — from (Prep = 0)</label>
        <input
          type="number"
          min={0}
          max={12}
          style={INPUT}
          value={edited.year_from ?? ""}
          onChange={(e) => setField("year_from", e.target.value)}
        />
      </div>
      <div>
        <label style={LABEL}>Year band — to</label>
        <input
          type="number"
          min={0}
          max={12}
          style={INPUT}
          value={edited.year_to ?? ""}
          onChange={(e) => setField("year_to", e.target.value)}
        />
      </div>
      <div>
        <label style={LABEL}>Loan / hire device</label>
        <select
          style={INPUT}
          value={edited.loan === true ? "yes" : edited.loan === false ? "no" : "unknown"}
          onChange={(e) =>
            setField(
              "loan",
              e.target.value === "yes" ? true : e.target.value === "no" ? false : null
            )
          }
        >
          <option value="unknown">Unknown</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>
      <div>
        <label style={LABEL}>Hardship provision</label>
        <select
          style={INPUT}
          value={edited.hardship === true ? "yes" : edited.hardship === false ? "no" : "unknown"}
          onChange={(e) =>
            setField(
              "hardship",
              e.target.value === "yes" ? true : e.target.value === "no" ? false : null
            )
          }
        >
          <option value="unknown">Unknown</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <label style={LABEL}>Loan note</label>
        <input
          style={INPUT}
          value={edited.loan_note}
          onChange={(e) => setField("loan_note", e.target.value)}
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <label style={LABEL}>Policy URL</label>
          {isHttpUrl(edited.policy_url) && (
            <a
              href={edited.policy_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: TEAL, fontWeight: 600 }}
            >
              Open ↗
            </a>
          )}
        </div>
        <input
          style={INPUT}
          value={edited.policy_url}
          onChange={(e) => setField("policy_url", e.target.value)}
          placeholder="https://…"
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <label style={LABEL}>Notes</label>
        <textarea
          style={Object.assign({}, INPUT, { minHeight: 54, resize: "vertical" })}
          value={edited.notes}
          onChange={(e) => setField("notes", e.target.value)}
        />
      </div>
    </div>
  );
}

export { ByodBandEditor };
